import { all, fork, put, takeEvery, call } from "redux-saga/effects";
import { SagaIterator } from "@redux-saga/core";

// helpers
import {
  getPackages as getPackageList,
  createPackageApi,
  getPackage as getPackageApi,
  updatePackageApi,
  deletePackageApi,
} from "../../helpers/";

// constants
import { PackagesActionTypes } from "./constants";
import {
  createPackageError,
  createPackageSuccess,
  packagesGetListError,
  packagesGetListSuccess,
  getPackageSuccess,
  getPackageError,
  updatePackageSuccess,
  updatePackageError,
  deletePackageSuccess,
  deletePackageError,
} from "./actions";

interface PackageData {
  payload: {
    limit: number;
    skip: number;
    keyword?: string;
  };
  type: string;
}

interface PackageCreateData {
  payload: {
    name: string;
    pack: { [countryCode: string]: number };
  };
  type: string;
}

interface PackageUpdateData {
  payload: {
    id: string;
    name: string;
    pack: { [countryCode: string]: number };
  };
  type: string;
}

interface PackageDeleteData {
  payload: {
    ids: string[];
  };
  type: string;
}

interface GetPackageData {
  payload: {
    id: string;
  };
  type: string;
}

function* getPackages({
  payload: { limit, skip, keyword },
}: PackageData): SagaIterator {
  try {
    const response = yield call(getPackageList, {
      limit,
      skip,
      keyword,
    });
    const packages = response.data;
    yield put(
      packagesGetListSuccess(PackagesActionTypes.GET_LIST_PACKAGES, packages)
    );
  } catch (error: any) {
    yield put(
      packagesGetListError(PackagesActionTypes.GET_LIST_PACKAGES, error)
    );
  }
}

function* createPackage({
  payload: { name, pack },
}: PackageCreateData): SagaIterator {
  try {
    const response = yield call(createPackageApi, {
      name,
      pack,
    });
    const packageData = response.data;
    yield put(
      createPackageSuccess(PackagesActionTypes.CREATE_PACKAGE, packageData)
    );
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data;
    yield put(
      createPackageError(PackagesActionTypes.CREATE_PACKAGE, errorMessage)
    );
  }
}

function* updatePackage({
  payload: { id, name, pack },
}: PackageUpdateData): SagaIterator {
  try {
    const response = yield call(updatePackageApi, {
      id,
      name,
      pack,
    });
    const packageData = response.data;
    yield put(
      updatePackageSuccess(PackagesActionTypes.UPDATE_PACKAGE, packageData)
    );
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data;
    yield put(
      updatePackageError(PackagesActionTypes.UPDATE_PACKAGE, errorMessage)
    );
  }
}

function* deletePackage({ payload: { ids } }: PackageDeleteData): SagaIterator {
  try {
    const response = yield call(deletePackageApi, {
      ids,
    });
    const packageData = response.data;
    yield put(
      deletePackageSuccess(PackagesActionTypes.DELETE_PACKAGE, packageData)
    );
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data;
    yield put(
      deletePackageError(PackagesActionTypes.DELETE_PACKAGE, errorMessage)
    );
  }
}

function* getPackage({ payload: { id } }: GetPackageData): SagaIterator {
  try {
    const response = yield call(getPackageApi, { id });
    const packageData = response.data;
    yield put(getPackageSuccess(PackagesActionTypes.GET_PACKAGE, packageData));
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data;
    yield put(getPackageError(PackagesActionTypes.GET_PACKAGE, errorMessage));
  }
}

export function* watchGetPackages() {
  yield takeEvery(PackagesActionTypes.GET_LIST_PACKAGES, getPackages);
}

export function* watchCreatePackages() {
  yield takeEvery(PackagesActionTypes.CREATE_PACKAGE, createPackage);
}

export function* watchUpdatePackages() {
  yield takeEvery(PackagesActionTypes.UPDATE_PACKAGE, updatePackage);
}

export function* watchDeletePackages() {
  yield takeEvery(PackagesActionTypes.DELETE_PACKAGE, deletePackage);
}

export function* watchGetPackage() {
  yield takeEvery(PackagesActionTypes.GET_PACKAGE, getPackage);
}

function* packagesSaga() {
  yield all([
    fork(watchGetPackages),
    fork(watchCreatePackages),
    fork(watchGetPackage),
    fork(watchUpdatePackages),
    fork(watchDeletePackages),
  ]);
}

export default packagesSaga;
